// Where in a coldroom a pallet or bin actually is — the ONE place that reads
// the (coldroom_id, coldroom_grid, coldroom_overflow) triple, so eight screens
// cannot each invent their own reading of it.
//
// THE FOUR STATES, and why they are four and not two (mig 20261021100001):
//
//   cell      a room + a grid cell. The normal case: 'A3'.
//   overflow  a room, no cell, coldroom_overflow = TRUE. Physically in the
//             room, deliberately not on the map, because the map was full.
//   nocell    a room, no cell, flag FALSE. NOT the same thing, and the whole
//             reason the flag exists rather than being derived from the NULL:
//             this is "in the room and nobody recorded where", which is a gap
//             to chase — an undispatch that dropped a cell, an old import. It
//             was reachable long before the overflow existed. Collapsing the
//             two would make a fault permanently indistinguishable from a
//             deliberate act, which is the one thing the register must not do.
//   noroom    no coldroom_id at all. Not in a room as far as we know.
//
// `label()` deliberately gives each of the four its own words. Nothing here
// returns '—' for the overflow: a dash is what the report prints for "no cell",
// and printing it for the overflow too would put fruit somebody consciously
// placed into the same bucket as fruit nobody recorded.

export const OVERFLOW_LABEL = 'Overflow'

/** What an operator picked in a placement UI: a cell, or the overflow area. */
export type ColdroomPlacement =
  | { kind: 'cell'; cell: string }
  | { kind: 'overflow' }

export type ColdroomLocationKind = 'cell' | 'overflow' | 'nocell' | 'noroom'

/** The shape every caller already has — pallets and bins carry the same trio. */
export interface ColdroomLocationRow {
  coldroom_id?:       string | null
  coldroom_grid?:     string | null
  // Optional so a query that predates this column (or deliberately does not
  // select it) reads as "not in the overflow" rather than crashing. Undefined
  // and false mean the same thing here, which is safe precisely because the
  // column is NOT NULL DEFAULT FALSE in the database.
  coldroom_overflow?: boolean | null
}

/**
 * Is this row in a room's overflow area? The single predicate — never test
 * `!coldroom_grid` for this, which is also true of the 'nocell' fault state.
 */
export function isInOverflow(row: ColdroomLocationRow): boolean {
  return !!row.coldroom_overflow && !!row.coldroom_id
}

export function locationKind(row: ColdroomLocationRow): ColdroomLocationKind {
  if (!row.coldroom_id) return 'noroom'
  if (row.coldroom_grid && row.coldroom_grid.trim()) return 'cell'
  return row.coldroom_overflow ? 'overflow' : 'nocell'
}

/**
 * What to print in a "Cell" column. Four distinct strings for four distinct
 * facts — see the header note on why the overflow does not get an em-dash.
 */
export function locationLabel(row: ColdroomLocationRow): string {
  switch (locationKind(row)) {
    case 'cell':     return row.coldroom_grid!.trim()
    case 'overflow': return OVERFLOW_LABEL
    case 'nocell':   return 'No cell'
    default:         return '—'
  }
}

/**
 * The column patch for a placement. Every write that places a pallet or bin
 * into a coldroom goes through this, so the invariant the database enforces
 * (overflow ⇒ no cell) is also the only thing the client can express.
 *
 * coldroom_overflow is written EXPLICITLY as false on a cell placement rather
 * than left out. Leaving it out would lean on the migration's stale-flag
 * trigger, which is a backstop for the writes we do not control (LoadingBay's
 * single-column floor-map move) and not a licence for the ones we do.
 */
export function placementPatch(coldroomId: string, placement: ColdroomPlacement): {
  coldroom_id:       string
  coldroom_grid:     string | null
  coldroom_overflow: boolean
} {
  return placement.kind === 'overflow'
    ? { coldroom_id: coldroomId, coldroom_grid: null, coldroom_overflow: true }
    : { coldroom_id: coldroomId, coldroom_grid: placement.cell.trim(), coldroom_overflow: false }
}

/** Has the operator finished choosing? Drives the Place button's disabled state. */
export function placementIsComplete(placement: ColdroomPlacement | null): boolean {
  if (!placement) return false
  return placement.kind === 'overflow' || placement.cell.trim().length > 0
}

/** "A3" / "Overflow" — what a confirm button says it is about to do. */
export function placementLabel(placement: ColdroomPlacement | null): string {
  if (!placement) return '…'
  return placement.kind === 'overflow' ? OVERFLOW_LABEL : (placement.cell.trim() || '…')
}

/**
 * The cell, or the overflow, or nothing — for a register cell or a chip on a
 * pallet card that shows a location ONLY when there is one. Unlike
 * `locationLabel` this never prints 'No cell' or an em-dash: those surfaces
 * (Pallets, Runs, Dispatch) list pallets in every status, and "no location"
 * is the ordinary state of a pallet that is not in cold, not a gap.
 *
 *   'A3'        on a cell
 *   'Overflow'  in the room, deliberately off the grid
 *   null        anything else — the caller renders nothing, as it always did
 */
export function cellOrOverflow(row: ColdroomLocationRow): string | null {
  const cell = row.coldroom_grid?.trim()
  if (cell) return cell
  return row.coldroom_overflow ? OVERFLOW_LABEL : null
}

/** `cellOrOverflow` worded for a chip: 'Grid A3' / 'Overflow' / null. */
export function coldroomChipLabel(row: ColdroomLocationRow): string | null {
  const v = cellOrOverflow(row)
  if (v == null) return null
  return v === OVERFLOW_LABEL ? v : `Grid ${v}`
}

/**
 * The coldroom map, opened on THIS row's room and cell (or its overflow) —
 * Coldroom reads ?room=&cell= / &overflow=1 and opens the drawer there. Null
 * when the row is not in a room, so the caller shows no link rather than one
 * to a map that can't show it. One rule for a bin's and a pallet's
 * "View map →" (ui-flow triage, 29 Sep: the pallet's location was plain text).
 */
export function coldroomMapHref(row: ColdroomLocationRow): string | null {
  if (!row.coldroom_id) return null
  const room = `/coldroom?room=${encodeURIComponent(row.coldroom_id)}`
  switch (locationKind(row)) {
    case 'cell':     return `${room}&cell=${encodeURIComponent((row.coldroom_grid as string).trim())}`
    case 'overflow': return `${room}&overflow=1`
    default:         return room
  }
}
